import type { Match, MatchRuleSet, MatchSide, MatchStatus, Team, TeamId } from '@/core/schemas';

/** German labels for the five match states required by the specification. */
export const MATCH_STATUS_LABELS: Readonly<Record<MatchStatus, string>> = {
  pending: 'wartet',
  called: 'aufgerufen',
  running: 'läuft',
  completed: 'beendet',
  conflict: 'Konflikt',
};

export const MATCH_STATUS_TONES: Readonly<Record<MatchStatus, string>> = {
  pending: 'bg-surface-overlay text-fg-muted',
  called: 'bg-warning-subtle text-warning',
  running: 'bg-info-subtle text-info',
  completed: 'bg-success-subtle text-success',
  conflict: 'bg-danger-subtle text-danger',
};

/** Name of a participant slot, including the placeholders of an open bracket. */
export function sideLabel(side: MatchSide, lookup: (teamId: TeamId) => Team | undefined): string {
  switch (side.kind) {
    case 'team':
      return lookup(side.teamId)?.name ?? 'Unbekanntes Team';
    case 'bye':
      return 'Freilos';
    case 'pending':
      return side.label;
  }
}

const ROUND_LABELS: Readonly<Record<string, string>> = {
  finale: 'Finale',
  halbfinale: 'Halbfinale',
  viertelfinale: 'Viertelfinale',
  achtelfinale: 'Achtelfinale',
  sechzehntelfinale: 'Sechzehntelfinale',
  'spiel-um-platz-3': 'Spiel um Platz 3',
};

/**
 * Turns a round key into a heading: `ko.finale` → "Finale",
 * `gruppenphase.spieltag-2` → "Spieltag 2".
 *
 * Kept in one place because schedule, bracket and beamer view all need it and
 * a naive "capitalise the first word" produced "Spiel um platz 3".
 */
export function roundLabel(roundKey: string): string {
  const slug = roundKey.split('.').slice(1).join('.') || roundKey;
  const known = ROUND_LABELS[slug];

  if (known !== undefined) {
    return known;
  }

  const numbered = /^(.*?)-(\d+)$/.exec(slug);
  if (numbered?.[1] !== undefined && numbered[2] !== undefined) {
    const word = numbered[1].replace(/-/g, ' ');
    return `${word.charAt(0).toUpperCase()}${word.slice(1)} ${numbered[2]}`;
  }

  const words = slug.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeRules(rules: MatchRuleSet | null): string {
  if (rules === null) {
    return '';
  }

  return rules.scoring === 'legs'
    ? `Best of ${rules.bestOf} Legs`
    : `Best of ${rules.bestOf} Sätze à ${rules.legsPerSet ?? '?'} Legs`;
}

/** Score as "2:1", or an empty string while the match is undecided. */
export function scoreLabel(match: Match): string {
  if (match.result === null) {
    return '';
  }

  const { legs, sets } = match.result.score;
  return sets === undefined
    ? `${legs.home}:${legs.away}`
    : `${sets.home}:${sets.away} (${legs.home}:${legs.away})`;
}

const ACTIVE_STATES: readonly MatchStatus[] = ['called', 'running'];

/** Matches currently on a board — the heart of the beamer view. */
export function activeMatches(matches: readonly Match[]): Match[] {
  return matches.filter((match) => ACTIVE_STATES.includes(match.status));
}

/**
 * The next match a team has to play.
 *
 * Matches whose participants are not yet decided are skipped: a captain wants
 * to know their next actual opponent, not that they might play someone.
 */
export function nextMatchFor(matches: readonly Match[], teamId: TeamId): Match | undefined {
  return matches
    .filter((match) => match.status !== 'completed')
    .filter(
      (match) =>
        (match.home.kind === 'team' && match.home.teamId === teamId) ||
        (match.away.kind === 'team' && match.away.teamId === teamId),
    )
    .sort((a, b) => a.order - b.order)[0];
}

export function matchesOfTeam(matches: readonly Match[], teamId: TeamId): Match[] {
  return matches.filter(
    (match) =>
      (match.home.kind === 'team' && match.home.teamId === teamId) ||
      (match.away.kind === 'team' && match.away.teamId === teamId),
  );
}
